import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { deleteGlobalRow, getGlobalGroups, listGlobalRows, saveGlobalRow, updateGlobalRow } from "../../services/appData";
import * as config from "../../config.js";
import { appDataGroupEndpoint, appDataRowEndpoint, sortRows } from "../../utils/appData";
import JsonTree from "./JsonTree";
import { SortHeader } from "./Sortable";
import RowEndpoint from "./RowEndpoint";
import Pager from "./Pager";
import DeleteRowDialog from "./DeleteRowDialog";
import BulkDeleteDialog from "./BulkDeleteDialog";
import { rowKey, useRowSelection } from "./selection";
import AppDataRowDialog from "./AppDataRowDialog";
import {
  Actions,
  Button,
  Card,
  Chip,
  Count,
  DataRow,
  ErrorBanner,
  GroupList,
  GroupRow,
  HeadRow,
  Mono,
  Muted,
  Notice,
  Row,
  Small,
  Split,
  Table,
  Toolbar,
  Truncate,
  Wrapper,
} from "./AppDataStyles";

const groupRows = (rows, field) => {
  const counts = new Map();

  rows.forEach((row) => {
    const key = row?.[field] ?? "(none)";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });

  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

/**
 * Global app data (`user_id IS NULL`), grouped by `name` or by `data_type`.
 *
 * The table leads with whichever field the rows are grouped by, and each row can be edited or
 * deleted in place.
 */
export const GlobalAppDataPanel = ({ clientId = null }) => {
  const [groupBy, setGroupBy] = useState("name");
  const [groups, setGroups] = useState([]);
  const [groupsPagination, setGroupsPagination] = useState(null);
  const [groupsPage, setGroupsPage] = useState(1);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [rowsPagination, setRowsPagination] = useState(null);
  const [rowsPage, setRowsPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [filter, setFilter] = useState("");
  const [searchRows, setSearchRows] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [sortKey, setSortKey] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");

  const term = filter.trim();
  const searchingActive = term.length > 0;

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (groupBy === "name") {
      const result = await getGlobalGroups({ clientId, page: groupsPage });

      setLoading(false);

      if (result?.success === false) {
        setGroups([]);
        setError(result.message || "Could not load the app data groups.");
        return;
      }

      setGroups(result.groups);
      setGroupsPagination(result.pagination ?? null);
      setAllRows([]);
      setSelectedGroup((current) =>
        current && result.groups.some((group) => group.name === current) ? current : result.groups[0]?.name ?? null
      );
      return;
    }

    // No endpoint groups by data_type, so read pages of rows and group them here (bounded so a
    // huge store doesn't stall the view).
    const collected = [];
    let page = 1;
    let lastResult = null;

    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const result = await listGlobalRows({ clientId, page, pageSize: 200 });
      lastResult = result;

      if (result?.success === false) break;

      collected.push(...result.records);

      if (!result.pagination?.hasNext || page >= 5) break;
      page += 1;
    }

    setLoading(false);

    if (lastResult?.success === false) {
      setGroups([]);
      setError(lastResult.message || "Could not load the app data rows.");
      return;
    }

    const grouped = groupRows(collected, "data_type");

    setAllRows(collected);
    setGroupsPagination(null);
    setGroups(grouped);
    setSelectedGroup((current) =>
      current && grouped.some((group) => group.name === current) ? current : grouped[0]?.name ?? null
    );
  }, [clientId, groupBy, groupsPage]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // Load the selected group's rows.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (searchingActive) {
        // The search results replace the group rows.
        setRows([]);
        return;
      }

      if (!selectedGroup) {
        setRows([]);
        return;
      }

      if (groupBy === "data_type") {
        const matching = allRows.filter((row) => (row.data_type ?? "(none)") === selectedGroup);
        const pageSize = 50;
        const start = (rowsPage - 1) * pageSize;

        setRows(matching.slice(start, start + pageSize));
        setRowsPagination({
          page: rowsPage,
          pageSize,
          total: matching.length,
          totalPages: Math.max(1, Math.ceil(matching.length / pageSize)),
          hasNext: start + pageSize < matching.length,
          hasPrevious: rowsPage > 1,
        });
        return;
      }

      setLoading(true);
      const result = await listGlobalRows({
        clientId,
        name: selectedGroup,
        page: filter ? 1 : rowsPage,
        pageSize: filter ? 500 : 50,
        sort: sortKey,
        direction: sortDir,
      });
      if (cancelled) return;

      setLoading(false);

      if (result?.success === false) {
        setRows([]);
        setError(result.message || "Could not load the group rows.");
        return;
      }

      setRows(sortRows(result.records, sortKey, sortDir));
      setRowsPagination(result.pagination ?? null);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [selectedGroup, groupBy, clientId, allRows, rowsPage, filter, searchingActive, sortKey, sortDir]);

  // Search every global row server-side: the group rows below only cover one name, so a key in
  // another group would otherwise never be found.
  useEffect(() => {
    if (!searchingActive) {
      setSearchRows([]);
      setSearchError(null);
      setSearching(false);
      return undefined;
    }

    let cancelled = false;
    setSearching(true);

    const handle = window.setTimeout(async () => {
      const result = await listGlobalRows({
        clientId,
        q: term,
        pageSize: 100,
        sort: sortKey,
        direction: sortDir,
      });

      if (cancelled) return;

      setSearching(false);

      if (result?.success === false) {
        setSearchRows([]);
        setSearchError(result.message || "Could not search app data.");
        return;
      }

      setSearchRows(sortRows(result.records, sortKey, sortDir));
      setSearchError(null);
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [term, searchingActive, clientId, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "updated_at" ? "desc" : "asc");
    }

    setRowsPage(1);
  };

  const openEdit = (row) => {
    setDialog({ mode: "edit", row });
    setDialogError(null);
  };

  const save = async (payload) => {
    setSaving(true);
    setDialogError(null);

    const result = await saveGlobalRow(payload, { clientId });

    setSaving(false);

    if (result?.success === false) {
      setDialogError(
        result?.status === 409
          ? "A row with that name and key already exists (create-only mode)."
          : result.message || "Could not save the row."
      );
      return;
    }

    setDialog(null);
    setNotice("Row saved.");
    setSelectedRow(null);
    await loadGroups();
  };

  const update = async (payload) => {
    if (!dialog?.row) return;

    setSaving(true);
    setDialogError(null);

    const result = await updateGlobalRow(dialog.row.name, dialog.row.key, payload, { clientId });

    setSaving(false);

    if (result?.success === false) {
      setDialogError(result.message || "Could not update the row.");
      return;
    }

    setDialog(null);
    setNotice("Row updated.");
    await loadGroups();
  };

  const remove = async (row, force = false) => {
    setDeleting(true);
    setDeleteError(null);

    const result = await deleteGlobalRow(row.name, row.key, { clientId, force });

    setDeleting(false);

    if (result?.success === false) {
      setDeleteError(result.message || "Could not delete the row.");
      return;
    }

    setPendingDelete(null);
    setSelectedRow(null);
    setNotice("Row deleted.");
    await loadGroups();
  };

  const needle = term.toLowerCase();

  const matchesRow = (row) =>
    !needle ||
    [row.name, row.key, row.data_type]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(needle));

  // Searching shows matches from every group; otherwise the selected group's rows.
  const sourceRows = searchingActive ? searchRows : rows;
  const visibleRows = sourceRows.filter(matchesRow);
  const selection = useRowSelection(visibleRows);

  const bulkDelete = async (force) => {
    const targets = selection.rows;

    if (targets.length === 0) return;

    setBulkBusy(true);
    setBulkError(null);

    let failed = 0;
    let lastError = null;

    for (const row of targets) {
      // eslint-disable-next-line no-await-in-loop
      const result = await deleteGlobalRow(row.name, row.key, { clientId, force });

      if (result?.success === false) {
        failed += 1;
        lastError = result.message;
      }
    }

    setBulkBusy(false);
    await loadGroups();

    if (failed > 0) {
      setBulkError(`${failed} of ${targets.length} failed${lastError ? `: ${lastError}` : ""}.`);
      return;
    }

    setBulkOpen(false);
    selection.clear();
    setNotice(`${targets.length} row${targets.length === 1 ? "" : "s"} deleted.`);
  };

  const visibleGroups = groups.filter(
    (group) => !needle || String(group.name).toLowerCase().includes(needle)
  );

  const groupingColumn = groupBy === "name" ? "name" : "data_type";

  // Only the selected grouping column is shown, then the row's key and metadata.
  const columns = "30px 1.4fr 0.8fr 70px 1.1fr auto";

  return (
    <Wrapper>
      <Toolbar>
        <span style={{ fontSize: "12.5px", color: "#a0a6b1" }}>Group by</span>
        <Small $active={groupBy === "name"} onClick={() => setGroupBy("name")}>Name</Small>
        <Small $active={groupBy === "data_type"} onClick={() => setGroupBy("data_type")}>Data type</Small>

        <input
          value={filter}
          placeholder="Search name, key or data type…"
          onChange={(event) => {
            setFilter(event.target.value);
            setRowsPage(1);
          }}
          style={{
            minWidth: "240px",
            padding: "5px 9px",
            fontSize: "12.5px",
            color: "var(--main-font-color)",
            backgroundColor: "var(--main-bg)",
            border: "1px solid var(--main-border-color)",
            borderRadius: "6px",
          }}
        />

        {selection.count > 0 ? (
          <>
            <Small
              $danger
              type="button"
              onClick={() => {
                setBulkOpen(true);
                setBulkError(null);
              }}
            >
              <Icon icon="lucide:trash-can-outline" /> Delete selected ({selection.count})
            </Small>
            <Small type="button" onClick={selection.clear}>
              Clear
            </Small>
          </>
        ) : null}

        <span style={{ flex: 1 }} />

        <Button
          type="button"
          onClick={() => {
            setDialog({ mode: "create", row: null });
            setDialogError(null);
          }}
        >
          <Icon icon="lucide:plus" /> New row
        </Button>
      </Toolbar>

      {error ? <ErrorBanner>{error}</ErrorBanner> : null}
      {searchError ? <ErrorBanner>{searchError}</ErrorBanner> : null}
      {searchingActive ? (
        <Muted>
          {searching ? "Searching all groups…" : `Search results across all groups (${visibleRows.length}).`}
        </Muted>
      ) : null}
      {notice ? <Notice>{notice}</Notice> : null}

      <Split>
        <Card>
          {loading ? (
            <Muted>Loading groups…</Muted>
          ) : visibleGroups.length === 0 ? (
            <Muted>{term ? "No groups match the search." : "No global app data yet."}</Muted>
          ) : (
            <GroupList>
              {visibleGroups.map((group) => (
                <GroupRow
                  key={group.name}
                  type="button"
                  $active={group.name === selectedGroup}
                  onClick={() => {
                    setSelectedGroup(group.name);
                    setRowsPage(1);
                    setSelectedRow(null);
                    setPendingDelete(null);
                  }}
                >
                  <Truncate>{group.name}</Truncate>
                  <Count>{group.count}</Count>
                </GroupRow>
              ))}
            </GroupList>
          )}

          {groupBy === "name" ? (
            <Pager
              pagination={groupsPagination}
              onPage={(page) => setGroupsPage(page)}
              loading={loading}
              label="groups"
            />
          ) : null}
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {groupBy === "name" && selectedGroup ? (
            <Card>
              <div style={{ fontSize: "11.5px", color: "#a0a6b1", marginBottom: "4px" }}>
                Retrieve the whole group
              </div>
              <RowEndpoint
                url={appDataGroupEndpoint({
                  baseUrl: config.REST_SERVER_URL,
                  name: selectedGroup,
                  clientId,
                })}
              />
            </Card>
          ) : null}

          <Table>
            <HeadRow style={{ gridTemplateColumns: columns }}>
              <input
                type="checkbox"
                checked={selection.allSelected}
                onChange={selection.toggleAll}
                title="Select all rows on this page"
                style={{ cursor: "pointer" }}
              />
              <SortHeader
                label={groupingColumn === "name" ? "Name" : "Data type"}
                column={groupingColumn}
                activeKey={sortKey}
                dir={sortDir}
                onToggle={toggleSort}
              />
              <SortHeader label="Key" column="key" activeKey={sortKey} dir={sortDir} onToggle={toggleSort} />
              <span>Locked</span>
              <SortHeader label="Updated" column="updated_at" activeKey={sortKey} dir={sortDir} onToggle={toggleSort} />
              <span />
            </HeadRow>

            {visibleRows.length === 0 ? (
              <Row style={{ gridTemplateColumns: "1fr", cursor: "default" }}>
                <Muted>
                  {searchingActive
                    ? "No rows match the search."
                    : selectedGroup
                      ? "No rows in this group."
                      : "Select a group."}
                </Muted>
              </Row>
            ) : (
              visibleRows.map((row) => (
                <React.Fragment key={row.public_id ?? `${row.name}/${row.key}`}>
                <DataRow
                  $active={selectedRow?.public_id === row.public_id}
                  style={{ gridTemplateColumns: columns }}
                  onClick={() => {
                    // Toggle: clicking the active row collapses it.
                    setSelectedRow((current) =>
                      current?.public_id === row.public_id ? null : row
                    );
                    setPendingDelete(null);
                  }}
                >
                  <span onClick={(event) => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selection.isSelected(rowKey(row))}
                      onChange={() => selection.toggle(rowKey(row))}
                      style={{ cursor: "pointer" }}
                    />
                  </span>
                  <Truncate>{groupingColumn === "name" ? row.name : row.data_type ?? "—"}</Truncate>
                  <Mono>{row.key}</Mono>
                  <span>{row.locked ? <Chip $tone="#f39c12">locked</Chip> : "—"}</span>
                  <span>{formatDate(row.updated_at)}</span>

                  <Actions>
                    <Small
                      type="button"
                      title="Edit this row"
                      onClick={(event) => {
                        event.stopPropagation();
                        openEdit(row);
                      }}
                    >
                      <Icon icon="lucide:pencil" />
                    </Small>
                    <Small
                      $danger
                      type="button"
                      title="Delete this row"
                      onClick={(event) => {
                        event.stopPropagation();
                        setPendingDelete(row);
                        setDeleteError(null);
                      }}
                    >
                      <Icon icon="lucide:trash-can-outline" />
                    </Small>
                  </Actions>
                </DataRow>

                {selectedRow?.public_id === row.public_id ? (
                  <div
                    style={{
                      padding: "10px 12px 14px",
                      borderBottom: "1px solid var(--content-item-border-color)",
                      backgroundColor: "var(--main-bg)",
                    }}
                  >
                    <RowEndpoint
                      url={appDataRowEndpoint({
                        baseUrl: config.REST_SERVER_URL,
                        scope: "global",
                        name: row.name,
                        key: row.key,
                        clientId,
                      })}
                    />

                    <div style={{ marginTop: "10px" }}>
                      <JsonTree value={row.data} />
                    </div>
                  </div>
                ) : null}
                </React.Fragment>
              ))
            )}
          </Table>

          {term ? null : (
            <Pager pagination={rowsPagination} onPage={(page) => setRowsPage(page)} loading={loading} />
          )}

        </div>
      </Split>

      <BulkDeleteDialog
        show={bulkOpen}
        count={selection.count}
        hasLocked={selection.rows.some((row) => row.locked)}
        busy={bulkBusy}
        error={bulkError}
        onClose={() => setBulkOpen(false)}
        onConfirm={bulkDelete}
      />

      <DeleteRowDialog
        show={Boolean(pendingDelete)}
        row={pendingDelete}
        scope="global"
        busy={deleting}
        error={deleteError}
        onClose={() => setPendingDelete(null)}
        onConfirm={(force) => remove(pendingDelete, force)}
      />

      <AppDataRowDialog
        show={Boolean(dialog)}
        mode={dialog?.mode ?? "create"}
        row={dialog?.row ?? null}
        scope="global"
        clientId={clientId}
        baseUrl={config.REST_SERVER_URL}
        defaultName={groupBy === "name" ? selectedGroup ?? "" : ""}
        saving={saving}
        error={dialogError}
        onClose={() => setDialog(null)}
        onSave={dialog?.mode === "edit" ? update : save}
      />
    </Wrapper>
  );
};

export default GlobalAppDataPanel;
